import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import ChatStore from 'stores/ChatStore';
import Input from 'components/Input/Input';
import { MESSAGE_CONFIG } from 'enum/messages';
import { generateID } from 'utils/helpers';

import ChatInputHandlers from './ChatInputHandlers/ChatInputHandlers';
import { IDS } from './utils';
import styles from './ChatInputMobileV2.css';


const makeId = generateID(IDS.prefix, IDS.element);

const ChatInputMobileV2 = (props) => {
  const {
    canProceedWithAction,
    isBroadcasterChat,
    isDesktop,
    isGroupShowChat,
    isPrivateChat,
    isPrivateUserChat,
    recipient,
    roomName,
    setIsInputFocused,
    user,
  } = props;

  const {
    activeConversation,
    addEmoji,
    banUserFromChatIfBroadcaster,
    chatInputValue,
    handleChatInput,
    isPrivateMessageOpen,
    sendGroupShowMessage,
    sendMainChatMessage,
    setActiveConversation,
  } = ChatStore;

  const isSpecialShowId = isBroadcasterChat && (isGroupShowChat || recipient);
  const inputId = makeId(isSpecialShowId ? IDS.specialShowChatInput : IDS.mainChatInput);

  const getChatInputId = useCallback(() => {
    document.getElementById(inputId).scrollLeft = document.getElementById(inputId).scrollWidth;
    // eslint-disable-next-line no-magic-numbers
    document.getElementById(inputId).setSelectionRange(1000, 1000);
  }, [inputId]);

  useEffect(() => {
    getChatInputId();
  }, [addEmoji, getChatInputId]);

  const handleInputFocus = useCallback((e) => {
    if (recipient) {
      setActiveConversation(recipient);
    }
    if (canProceedWithAction()) {
      setIsInputFocused(true);
    } else {
      setIsInputFocused(false);
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        e.target.blur();
      }
    }
  }, [
    canProceedWithAction,
    recipient,
    setIsInputFocused,
    setActiveConversation,
  ]);

  const handleLovenseInput = useCallback((e) => {
    if (canProceedWithAction()) {
      setIsInputFocused(true);
    } else {
      setIsInputFocused(false);
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        e.target.blur();
      }
    }
  }, [setIsInputFocused, canProceedWithAction]);

  const onInputBlur = useCallback(() => {
    setIsInputFocused(false);
  }, [setIsInputFocused]);

  const handleChange = useCallback((e) => {
    if (e.key === 'Enter' && !banUserFromChatIfBroadcaster(user.username, user.accessLevel, roomName)) {
      if (isPrivateUserChat || recipient) {
        sendMainChatMessage(
          user.username,
          user.accessHash,
          roomName,
          chatInputValue,
          recipient
        );
      } else if (isGroupShowChat && !isPrivateMessageOpen) {
        sendGroupShowMessage(
          user.username,
          user.accessHash,
          roomName,
          chatInputValue
        );
      } else {
        sendMainChatMessage(
          user.username,
          user.accessHash,
          roomName,
          chatInputValue
        );
      }
    }
  }, [
    banUserFromChatIfBroadcaster,
    chatInputValue,
    isGroupShowChat,
    isPrivateMessageOpen,
    isPrivateUserChat,
    recipient,
    roomName,
    sendGroupShowMessage,
    sendMainChatMessage,
    user.accessHash,
    user.accessLevel,
    user.username,
  ]);

  const handleChatInputChange = useCallback(
    (e) => {
      if (canProceedWithAction()) {
        handleChatInput(e.target.value);
      }
    },
    [handleChatInput, canProceedWithAction],
  );

  const handleLovenseChange = useCallback((e) => {
    if (e.key === 'Enter') {
      sendMainChatMessage(
        user.username,
        user.accessHash,
        roomName,
        chatInputValue,
        undefined
      );
    }
  }, [
    chatInputValue,
    roomName,
    sendMainChatMessage,
    user,
  ]);

  const loveInputUnfocus = useCallback(() => {
    setIsInputFocused(false);
  }, [setIsInputFocused]);

  const handleLovenseInputChange = useCallback((e) => {
    handleChatInput(e.target.value);
  }, [handleChatInput]);

  const containerClasses = classNames({
    [styles.chatInputWrapperPrivate]: isPrivateChat,
    [styles.desktopVersion]: isDesktop,
    [styles.chatInputWrapper]: true,
    [styles.customActiveConversationInputWrapper]: activeConversation && !isDesktop,
  });

  const chatInputClasses = classNames({
    [styles.chatInput]: true,
    [styles.chatInputActivePrivateConversation]: activeConversation && !isDesktop,
  });

  return (
    <div aria-describedby="giphyPopper" className={containerClasses}>
      <Input
        autoComplete="off"
        autoFocus={Boolean(isDesktop)}
        customWrapperClass={styles.customInputWrapper}
        id={inputId}
        inputClass={chatInputClasses}
        maxLength={MESSAGE_CONFIG.MAX_LENGTH}
        placeholder={translate('v2profile.v2.message.input.placeholder')}
        value={chatInputValue}
        onBlur={onInputBlur}
        onChange={handleChatInputChange}
        onClick={handleInputFocus}
        onKeyDown={handleChange}
      />
      <div className={styles.messageActions}>
        <ChatInputHandlers
          canProceedWithAction={canProceedWithAction}
          inputId={inputId}
          isBroadcasterChat={isBroadcasterChat}
          isDesktop={isDesktop}
          isGroupShowChat={isGroupShowChat}
          isPrivateChat={isPrivateChat}
          message={chatInputValue}
          recipient={recipient}
          roomName={roomName}
          user={user}
        />
      </div>
      {isSpecialShowId &&
        <Input
          customWrapperClass={styles.lovensePublicChat}
          id={makeId(IDS.mainChatInput)}
          maxLength={MESSAGE_CONFIG.MAX_LENGTH}
          onBlur={loveInputUnfocus}
          onChange={handleLovenseInputChange}
          onClick={handleLovenseInput}
          onKeyDown={handleLovenseChange}
        />
      }
    </div>
  );
};

ChatInputMobileV2.propTypes = {
  canProceedWithAction: PropTypes.func.isRequired,
  isBroadcasterChat: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isGroupShowChat: PropTypes.bool,
  isPrivateChat: PropTypes.bool,
  isPrivateUserChat: PropTypes.bool,
  recipient: PropTypes.string,
  roomName: PropTypes.string,
  setIsInputFocused: PropTypes.func,
  user: PropTypes.object,
};

ChatInputMobileV2.defaultProps = {
  isBroadcasterChat: false,
  isDesktop: false,
  isGroupShowChat: false,
  isPrivateChat: false,
  isPrivateUserChat: false,
  recipient: undefined,
  roomName: undefined,
  setIsInputFocused: () => undefined,
  user: undefined,
};

export default observer(ChatInputMobileV2);
